import { AuditLog } from "../interfaces/models/IAudityLogs";

const STAFF_ROLE_KEYS = new Set([
  "wsgStaff",
  "wsgSuper",
  "WSG Staff",
  "WSG Staff Super",
]);

function isStaffRole(roles?: string[]): boolean {
  if (!Array.isArray(roles) || roles.length === 0) {
    return false;
  }
  return roles.some((role) => STAFF_ROLE_KEYS.has(String(role).trim()));
}

function initialsFromFullName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function formatAuditActorDisplay(
  log: Pick<AuditLog, "userName" | "userId" | "role">,
  options?: {
    registrantDisplayName?: string;
    registrantUserId?: string;
  },
): string {
  const raw = String(log.userName ?? "").trim();
  const staff = isStaffRole(log.role);

  // Already stored in the desired format by the API
  if (/^WSG\s*-\s*[A-Za-z]{1,4}$/i.test(raw)) {
    const initials = raw.split("-")[1]?.trim().toUpperCase() ?? "";
    return initials ? `WSG - ${initials}` : "WSG";
  }

  if (staff) {
    if (raw.includes(" ")) {
      const initials = initialsFromFullName(raw);
      return initials ? `WSG - ${initials}` : "WSG";
    }
    // Legacy sign-in username: best-effort two-letter code
    const letters = raw.replace(/[^A-Za-z]/g, "");
    if (letters.length >= 2) {
      return `WSG - ${letters.slice(0, 2).toUpperCase()}`;
    }
    if (letters.length === 1) {
      return `WSG - ${letters.toUpperCase()}`;
    }
    return "WSG";
  }

  // Member / registrant
  if (raw.includes(" ")) {
    return raw;
  }

  const registrantName = options?.registrantDisplayName?.trim();
  if (
    registrantName &&
    options?.registrantUserId &&
    log.userId &&
    options.registrantUserId === log.userId
  ) {
    return registrantName;
  }

  // Common case: member self-registration logs may only have sign-in name;
  // prefer registrant full name when available.
  if (registrantName) {
    return registrantName;
  }

  return raw || log.userId || "";
}

export function formatStaffActorLabel(
  name?: string | null,
  lastName?: string | null,
): string {
  const initials = `${String(name ?? "")
    .trim()
    .charAt(0)}${String(lastName ?? "")
    .trim()
    .charAt(0)}`.toUpperCase();
  return initials.trim() ? `WSG - ${initials}` : "WSG";
}

export function formatPersonActorLabel(
  name?: string | null,
  lastName?: string | null,
  fallback?: string,
): string {
  const full = [name, lastName]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return full || fallback?.trim() || "Unknown";
}
